import { forwardRef, useEffect, useMemo, useState } from 'react';

import type { NumberInputProps as MantineInputProps } from '@mantine/core';
import { NumberInput as MantineInput } from '@mantine/core';

import { useCurrencyHandler } from './useCurrencyHandler';

type NumberInputProps = MantineInputProps;

const NumberInput = MantineInput;

const NumberInputDefaults = NumberInput.extend({
  defaultProps: {
    radius: 'sm',
    hideControls: true,
  },
});

interface CurrencyProps extends Omit<NumberInputProps, 'children'> {
  asCents?: boolean;
  children: (props: CurrencyPropsChildren) => React.ReactNode | React.ReactNode[];
}

interface CurrencyPropsChildren extends NumberInputProps {
  value: number | string;
  onChange: (value: number | string) => void;
}
export interface CurrencyInputProps extends NumberInputProps {
  experimental_showDecimalKeyboard?: boolean;
}

// This can now be used as a wrapper around anything that would need to translate currency to/from cents.
//  This can also be used to expand that functionality to other currencies if needed
const Currency = ({ asCents = true, children, value, onChange, ...rest }: CurrencyProps) => {
  const [_value, _setValue] = useState<string | number>('');
  const { transform } = useCurrencyHandler();

  const handleInputChange = (value: number | string) => {
    if (value || value === 0) {
      onChange?.(transform(value, 'cents'));
    } else {
      // No value, set to empty string
      onChange?.('');
    }
  };

  useEffect(() => {
    if (value || value === 0) {
      if (asCents) {
        _setValue(transform(value, 'dollars'));
      } else {
        _setValue(value);
      }
    } else {
      _setValue('');
    }
  }, [asCents, transform, value]);

  return <>{children({ value: _value, onChange: handleInputChange, ...rest })}</>;
};

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ experimental_showDecimalKeyboard, ...props }, ref) => {
    const inputMode = useMemo(
      () =>
        props.allowDecimal === false || experimental_showDecimalKeyboard !== true
          ? undefined
          : 'decimal',
      [experimental_showDecimalKeyboard, props.allowDecimal]
    );
    return (
      <Currency {...props}>
        {(props) => (
          <NumberInput
            ref={ref}
            label="Enter Amount"
            placeholder="$ 0.00"
            decimalScale={2}
            min={0}
            hideControls
            clampBehavior="blur"
            prefix="$ "
            inputMode={inputMode}
            {...props}
          />
        )}
      </Currency>
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';

export { Currency, CurrencyInput, NumberInput, NumberInputDefaults };
